import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Plus, Printer } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SectionCard, EmptyState, ConfirmDelete, KpiCard, TABLE_HEAD, TABLE_ROW } from "./common";
import { SimpleSelect } from "./DepotField";
import { CartEditor, cartItems } from "./CartEditor";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, money, dateTimeFmt, daysAgoStr, todayStr, downloadFile, TZ, COMPANY, CASH_METHODS, TRUCK_EXPENSE_CATEGORIES } from "../lib/api";

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="label-mono">{label}</Label>{children}</div>;

function TruckSelect({ trucks, value, onChange, testid }) {
  return (
    <F label="Truck">
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="bg-slate-950" data-testid={testid}><SelectValue placeholder="Select truck" /></SelectTrigger>
        <SelectContent>{trucks.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`${testid}-option-${t.plate}`}>{t.plate}{t.driver ? ` · ${t.driver}` : ""}</SelectItem>)}</SelectContent>
      </Select>
    </F>
  );
}

function useRange() {
  const [start, setStart] = useState(daysAgoStr(29));
  const [end, setEnd] = useState(todayStr());
  const bar = (
    <div className="flex items-center gap-2">
      <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} className="h-9 w-[150px] bg-slate-950" data-testid="truck-range-start" />
      <span className="text-slate-500">→</span>
      <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="h-9 w-[150px] bg-slate-950" data-testid="truck-range-end" />
    </div>
  );
  return { start, end, bar };
}

async function post(path, body, msg, done) {
  try { await api.post(path, body); toast.success(msg); done(); return true; } catch (err) { toast.error(formatApiError(err)); return false; }
}

export function FleetTab() {
  const { isAdmin } = useAuth();
  const { data, reload } = useApi("/trucks");
  const [f, setF] = useState({ plate: "", model: "", driver: "" });
  const save = async (e) => {
    e.preventDefault();
    if (await post("/trucks", f, `Truck ${f.plate} added`, reload)) setF({ plate: "", model: "", driver: "" });
  };
  const setStatus = (t, status) => api.put(`/trucks/${t.id}`, { plate: t.plate, model: t.model, driver: t.driver, note: t.note, status }).then(() => { toast.success(`${t.plate} marked ${status}`); reload(); }).catch((err) => toast.error(formatApiError(err)));
  const remove = (id) => api.delete(`/trucks/${id}`).then(() => { toast.success("Truck removed"); reload(); }).catch((err) => toast.error(formatApiError(err)));
  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
      <SectionCard title="Add truck" className="xl:col-span-4 self-start">
        <form onSubmit={save} className="space-y-4 p-5" data-testid="truck-form">
          <F label="Plate number"><Input required value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value })} placeholder="e.g. T 123 ABC" className="bg-slate-950" data-testid="truck-plate-input" /></F>
          <F label="Model"><Input value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} className="bg-slate-950" data-testid="truck-model-input" /></F>
          <F label="Driver"><Input value={f.driver} onChange={(e) => setF({ ...f, driver: e.target.value })} className="bg-slate-950" data-testid="truck-driver-input" /></F>
          <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="truck-submit-button"><Plus className="h-4 w-4" /> Add truck</Button>
        </form>
      </SectionCard>
      <SectionCard title={`${data?.length ?? 0} trucks`} className="xl:col-span-8">
        {!data?.length ? <EmptyState text="No trucks yet" testid="trucks-empty" /> : (
          <table className="w-full text-sm" data-testid="trucks-table">
            <thead className={TABLE_HEAD}><tr><th className="px-4 py-2 text-left">Plate</th><th className="px-4 py-2 text-left">Model / Driver</th><th className="px-4 py-2 text-left">Status</th><th /></tr></thead>
            <tbody>{data.map((t) => (
              <tr key={t.id} className={TABLE_ROW} data-testid={`truck-row-${t.plate}`}>
                <td className="px-4 py-2 font-mono font-semibold text-amber-400">{t.plate}</td>
                <td className="px-4 py-2"><div>{t.model || "—"}</div><div className="text-xs text-slate-500">{t.driver || "No driver"}</div></td>
                <td className="px-4 py-2 w-44">
                  <Select value={t.status} onValueChange={(v) => setStatus(t, v)}>
                    <SelectTrigger className="h-8 bg-slate-950" data-testid={`truck-status-${t.plate}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                  </Select>
                </td>
                <td className="px-2">{isAdmin && <ConfirmDelete title={`Remove ${t.plate}?`} description="Its trips and expenses stay in the records." onConfirm={() => remove(t.id)} testid={`delete-truck-${t.plate}`} />}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </SectionCard>
    </div>
  );
}

function LedgerTable({ rows, cols, onDelete, testid }) {
  if (!rows?.length) return <EmptyState text="Nothing in this period" testid={`${testid}-empty`} />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className={TABLE_HEAD}><tr><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Truck</th>{cols.map(([h]) => <th key={h} className="px-4 py-2 text-left">{h}</th>)}<th className="px-4 py-2 text-right">Amount</th><th /></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className={TABLE_ROW}>
            <td className="px-4 py-2 font-mono text-xs text-slate-400 whitespace-nowrap">{dateTimeFmt(r.created_at)}</td>
            <td className="px-4 py-2 font-mono text-amber-400">{r.plate}</td>
            {cols.map(([h, fn]) => <td key={h} className="px-4 py-2 text-slate-300">{fn(r)}</td>)}
            <td className="px-4 py-2 text-right font-mono">{money(r.amount)}</td>
            <td className="px-2">{onDelete && <ConfirmDelete title="Delete entry?" description="This entry will be removed from truck reports." onConfirm={() => onDelete(r.id)} testid={`delete-${testid}-${r.id}`} />}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function LedgerTab({ kind }) {
  const trip = kind === "trips";
  const { isAdmin } = useAuth();
  const trucks = useApi("/trucks");
  const { start, end, bar } = useRange();
  const list = useApi(`/trucks/${kind}`, { start, end, tz: TZ });
  const blank = { truck_id: "", customer: "", route: "", category: TRUCK_EXPENSE_CATEGORIES[0], amount: "", payment_method: "Cash", note: "" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!f.truck_id) { toast.error("Select a truck"); return; }
    setBusy(true);
    const body = trip ? { truck_id: f.truck_id, customer: f.customer, route: f.route, amount: Number(f.amount), payment_method: f.payment_method, note: f.note }
      : { truck_id: f.truck_id, category: f.category, amount: Number(f.amount), payment_method: f.payment_method, note: f.note };
    if (await post(`/trucks/${kind}`, body, trip ? "Trip income recorded" : "Truck expense recorded", list.reload)) setF({ ...blank, truck_id: f.truck_id });
    setBusy(false);
  };
  const remove = (id) => api.delete(`/trucks/${kind}/${id}`).then(() => { toast.success("Deleted"); list.reload(); }).catch((err) => toast.error(formatApiError(err)));
  const total = (list.data || []).reduce((s, r) => s + r.amount, 0);
  const p = trip ? "trip" : "truck-expense";
  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
      <SectionCard title={trip ? "Record trip / hire" : "Record truck expense"} className="xl:col-span-4 self-start">
        <form onSubmit={save} className="space-y-4 p-5" data-testid={`${p}-form`}>
          <TruckSelect trucks={trucks.data || []} value={f.truck_id} onChange={(v) => setF({ ...f, truck_id: v })} testid={`${p}-truck-select`} />
          {trip ? (
            <>
              <F label="Customer"><Input value={f.customer} onChange={(e) => setF({ ...f, customer: e.target.value })} placeholder="Optional" className="bg-slate-950" data-testid="trip-customer-input" /></F>
              <F label="Route"><Input required value={f.route} onChange={(e) => setF({ ...f, route: e.target.value })} placeholder="e.g. Dar es Salaam → Dodoma" className="bg-slate-950" data-testid="trip-route-input" /></F>
            </>
          ) : <SimpleSelect label="Category" value={f.category} onChange={(v) => setF({ ...f, category: v })} options={TRUCK_EXPENSE_CATEGORIES} testid="truck-expense-category-select" />}
          <div className="grid grid-cols-2 gap-4">
            <F label="Amount (TSh)"><Input type="number" min="1" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="bg-slate-950" data-testid={`${p}-amount-input`} /></F>
            <SimpleSelect label={trip ? "Paid via" : "Paid with"} value={f.payment_method} onChange={(v) => setF({ ...f, payment_method: v })} options={CASH_METHODS} testid={`${p}-payment-select`} />
          </div>
          <F label="Note"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Optional" className="bg-slate-950" data-testid={`${p}-note-input`} /></F>
          <Button type="submit" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid={`${p}-submit-button`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </Button>
        </form>
      </SectionCard>
      <SectionCard className="xl:col-span-8" title={<span>{trip ? "Trip income" : "Truck expenses"} · <span className="font-mono" data-testid={`${p}-total`}>{money(total)}</span></span>} right={bar}>
        <LedgerTable rows={list.data} testid={`${p}-table`} onDelete={isAdmin ? remove : null}
          cols={trip ? [["Route", (r) => r.route], ["Customer", (r) => r.customer || "—"], ["Paid", (r) => r.payment_method]]
            : [["Category", (r) => r.category], ["Note", (r) => (r.receipt_no ? `${r.receipt_no} · ` : "") + (r.note || "—")], ["Paid", (r) => r.payment_method]]} />
      </SectionCard>
    </div>
  );
}

export const TripsTab = () => <LedgerTab kind="trips" />;
export const ExpensesTab = () => <LedgerTab kind="expenses" />;

export function RequisitionTab() {
  const trucks = useApi("/trucks");
  const products = useApi("/trucks/products");
  const [truckId, setTruckId] = useState("");
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!truckId || !lines.length) { toast.error("Select a truck and add products"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/trucks/requisitions", { truck_id: truckId, note, items: cartItems(lines).map(({ product_id, qty }) => ({ product_id, qty })) });
      toast.success(`Taken from Dar es Salaam store · ${money(data.total)} charged to truck (${data.receipt_no})`);
      setLines([]); setNote(""); products.reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <SectionCard title="Take products from Dar es Salaam hardware">
      <form onSubmit={submit} className="space-y-4 p-5 max-w-3xl" data-testid="requisition-form">
        <p className="text-sm text-slate-400">Items are charged at selling price: the hardware gets the cash and the truck gets a "Hardware supplies" expense.</p>
        <TruckSelect trucks={trucks.data || []} value={truckId} onChange={setTruckId} testid="requisition-truck-select" />
        <CartEditor products={products.data || []} lines={lines} setLines={setLines} lockPrice testid="requisition-cart" />
        <F label="Note"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Engine oil for service" className="bg-slate-950" data-testid="requisition-note-input" /></F>
        <Button type="submit" disabled={busy} className="h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="requisition-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Take products &amp; charge truck
        </Button>
      </form>
    </SectionCard>
  );
}

function StatementSheet({ s }) {
  const t = s.truck;
  return (
    <div className="print-sheet rounded-lg bg-white p-6 text-slate-900 sm:p-10" data-testid="truck-statement-sheet">
      <div className="border-b-2 border-slate-900 pb-4">
        <div className="font-display text-2xl font-extrabold uppercase">{COMPANY.name} · Truck Statement</div>
        <div className="text-sm text-slate-600">{COMPANY.address} · {COMPANY.email} · TIN {COMPANY.tin} · VRN {COMPANY.vrn}</div>
        <div className="mt-2 text-sm"><b className="font-mono" data-testid="statement-plate">{t.plate}</b> · {t.model || "—"} · Driver: {t.driver || "—"} · {t.status}</div>
        <div className="text-sm">Period {s.start} to {s.end} · Generated by {s.generated_by}</div>
      </div>
      <table className="rpt-table mt-4"><thead><tr><th>Trips</th><th>Trip income</th><th>Expenses</th><th>Net profit</th></tr></thead>
        <tbody><tr><td>{s.trips.length}</td><td data-testid="statement-income">{money(s.income)}</td><td data-testid="statement-expenses">{money(s.expenses_total)}</td><td><b data-testid="statement-net">{money(s.net)}</b></td></tr></tbody></table>
      <h3 className="mt-6 font-bold">Trips ({s.trips.length})</h3>
      <table className="rpt-table mt-2" data-testid="statement-trips-table"><thead><tr><th>Date</th><th>Route</th><th>Customer</th><th>Paid</th><th>Amount</th></tr></thead>
        <tbody>{s.trips.map((x) => <tr key={x.id}><td>{dateTimeFmt(x.created_at)}</td><td>{x.route}</td><td>{x.customer || "—"}</td><td>{x.payment_method}</td><td>{money(x.amount)}</td></tr>)}
          <tr><td><b>TOTAL</b></td><td /><td /><td /><td><b>{money(s.income)}</b></td></tr></tbody></table>
      <h3 className="mt-6 font-bold">Expenses ({s.expenses.length})</h3>
      <table className="rpt-table mt-2" data-testid="statement-expenses-table"><thead><tr><th>Date</th><th>Category</th><th>Note</th><th>Paid</th><th>Amount</th></tr></thead>
        <tbody>{s.expenses.map((x) => <tr key={x.id}><td>{dateTimeFmt(x.created_at)}</td><td>{x.category}</td><td>{(x.receipt_no ? `${x.receipt_no} · ` : "") + (x.note || "—")}</td><td>{x.payment_method}</td><td>{money(x.amount)}</td></tr>)}
          <tr><td><b>TOTAL</b></td><td /><td /><td /><td><b>{money(s.expenses_total)}</b></td></tr></tbody></table>
      {Object.keys(s.by_category).length > 0 && (
        <table className="rpt-table mt-6"><thead><tr><th>Expense category</th><th>Amount</th></tr></thead>
          <tbody>{Object.entries(s.by_category).map(([k, v]) => <tr key={k}><td>{k}</td><td>{money(v)}</td></tr>)}</tbody></table>
      )}
    </div>
  );
}

export function StatementTab() {
  const trucks = useApi("/trucks");
  const [truckId, setTruckId] = useState("");
  const { start, end, bar } = useRange();
  const { data } = useApi(truckId ? `/trucks/${truckId}/statement` : null, { start, end, tz: TZ });
  const download = () => downloadFile(`/trucks/${truckId}/statement/pdf`, { start, end, tz: TZ }, `truck-statement-${data.truck.plate.replace(/\s/g, "")}-${start}-to-${end}.pdf`)
    .then(() => toast.success("PDF downloaded")).catch((err) => toast.error(formatApiError(err)));
  return (
    <div className="space-y-6">
      <div className="no-print flex flex-wrap items-end gap-3">
        <div className="w-60"><TruckSelect trucks={trucks.data || []} value={truckId} onChange={setTruckId} testid="statement-truck-select" /></div>
        {bar}
        <Button variant="outline" className="border-slate-700" disabled={!data} onClick={() => window.print()} data-testid="statement-print-button"><Printer className="h-4 w-4" /> Print</Button>
        <Button className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" disabled={!data} onClick={download} data-testid="statement-pdf-button"><Download className="h-4 w-4" /> PDF</Button>
      </div>
      {!truckId ? <EmptyState text="Select a truck to see its statement" testid="statement-empty" /> : data && data.truck.id === truckId && <StatementSheet s={data} />}
    </div>
  );
}

export function ReportTab() {
  const { start, end, bar } = useRange();
  const { data } = useApi("/trucks/summary", { start, end, tz: TZ });
  return (
    <div className="space-y-6">
      <div className="no-print flex flex-wrap items-center gap-3">{bar}
        <Button variant="outline" className="border-slate-700" onClick={() => window.print()} data-testid="truck-report-print-button"><Printer className="h-4 w-4" /> Print</Button>
      </div>
      {data && (
        <>
          <div className="no-print grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label="Trip income" value={money(data.income)} tone="emerald" testid="truck-report-income" />
            <KpiCard label="Truck expenses" value={money(data.expenses)} tone="rose" testid="truck-report-expenses" />
            <KpiCard label="Net" value={money(data.net)} tone="amber" testid="truck-report-net" />
            <KpiCard label="Trips" value={data.trips} tone="slate" testid="truck-report-trips" />
          </div>
          <div className="print-sheet rounded-lg bg-white p-6 text-slate-900 sm:p-10" data-testid="truck-report-sheet">
            <div className="border-b-2 border-slate-900 pb-4">
              <div className="font-display text-2xl font-extrabold uppercase">{COMPANY.name} · Truck Fleet Report</div>
              <div className="text-sm text-slate-600">{COMPANY.address} · {COMPANY.email}</div>
              <div className="text-sm">Period {data.start} to {data.end} · Generated by {data.generated_by}</div>
            </div>
            <table className="rpt-table mt-4">
              <thead><tr><th>Truck</th><th>Driver</th><th>Status</th><th>Trips</th><th>Income</th><th>Expenses</th><th>Net</th></tr></thead>
              <tbody>
                {data.rows.map((r) => <tr key={r.truck_id}><td>{r.plate}</td><td>{r.driver || "—"}</td><td>{r.status}</td><td>{r.trips}</td><td>{money(r.income)}</td><td>{money(r.expenses)}</td><td>{money(r.net)}</td></tr>)}
                <tr><td><b>TOTAL</b></td><td /><td /><td><b>{data.trips}</b></td><td><b>{money(data.income)}</b></td><td><b>{money(data.expenses)}</b></td><td><b>{money(data.net)}</b></td></tr>
              </tbody>
            </table>
            <table className="rpt-table mt-6">
              <thead><tr><th>Expense category</th><th>Amount</th></tr></thead>
              <tbody>{Object.entries(data.by_category).map(([k, v]) => <tr key={k}><td>{k}</td><td>{money(v)}</td></tr>)}</tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
